/**
 * The setup run as a checklist a person can read before it finishes: seven
 * rows at most, each labelled from the configuration the run is actually
 * using, each carrying the state the record knows about.
 *
 * Records written before runs kept `stages` still render: their single legacy
 * stage name places the run on the list, everything before it reads as done
 * and everything after it as pending.
 */
/**
 * Where a legacy `stage` string sits on the list. `complete` sits past the
 * last row the run itself reaches: a finished run has done everything except
 * be put to use, which is activation's job.
 */
export function legacyStageKey(stage) {
    switch (stage) {
        case "queued":
        case "configure":
        case "inspect":
        case "revision":
            return "read";
        // `startup` covers installation and service start in one legacy name. It
        // resolves to the earlier of the two so the list never claims that
        // installing dependencies succeeded when it may be what failed.
        case "startup":
            return "install";
        case "tests":
            return "tests";
        case "authentication":
        case "authentication_complete":
        case "waiting_for_human":
            return "sign_in";
        case "browser":
        case "first_check":
        case "deployment":
            return "first_check";
        case "complete":
            return "end";
        default:
            return null;
    }
}
function serviceLabel(config) {
    const services = config.services.map((service) => `${service.name} on port ${service.port}`);
    if (!services.length)
        return "Start the app";
    const last = services[services.length - 1];
    return `Start ${services.length === 1 ? last : `${services.slice(0, -1).join(", ")} and ${last}`}`;
}
function signInLabel(config) {
    switch (config.authMode) {
        case "command":
        case "recipe":
        case "bootstrap":
            return "Sign in as the test user";
        case "cognito":
            return "Sign in with the Cognito test identity";
        case "human":
            return "Sign in yourself in a private browser";
        default:
            return null;
    }
}
function firstCheckLabel(config) {
    const expectation = config.firstCheck?.text ?? config.firstCheck?.selector;
    return expectation
        ? `Open ${config.entryPath} and see ${expectation}`
        : `Open ${config.entryPath} and confirm it loads`;
}
/**
 * The sentence for a build-only run that went on without some of its
 * services: each one (with why, when known), then what that means for
 * builds. The run's start stage and the checklist both read it.
 */
export function servicesNotStartedSentence(services, options = {}) {
    const each = options.reasons
        ? services.map((service) => `${service.name} did not start. ${service.reason}`)
        : [`${services.map((service) => service.name).join(", ")} did not start.`];
    return [
        ...each,
        `Builds install and test without ${services.length === 1 ? "it" : "them"}.`,
    ].join(" ");
}
export function qaChecklist(config, validation) {
    // The verify command is the suite this environment gates on; a browser
    // suite alone still deserves its row.
    const suite = config.verifyCommand ?? config.browserTestCommand;
    const signIn = signInLabel(config);
    const planned = [
        { key: "read", label: "Read the repository" },
        {
            key: "install",
            label: "Install dependencies",
            detail: config.setupCommands[0],
        },
        { key: "start", label: serviceLabel(config) },
        ...(suite ? [{ key: "tests", label: `Run ${suite}` }] : []),
        ...(signIn ? [{ key: "sign_in", label: signIn }] : []),
        { key: "first_check", label: firstCheckLabel(config) },
        { key: "use", label: "Put to work for PR checks and builds" },
    ];
    // A build-only run opens no browser, so its first check (and the recording
    // that goes with it) is skipped rather than pending forever.
    const buildOnly = validation?.kind
        ? validation.kind === "build"
        : config.browser === false;
    const suiteResult = validation?.suite;
    const notStarted = validation?.servicesNotStarted ?? [];
    return recordedRows(planned, validation).map((row) => {
        if (buildOnly && row.key === "first_check")
            return { ...row, state: "skipped" };
        // A build-only run went on without a service that did not start: a
        // warning on the start row, whose detail says which and why.
        if (row.key === "start" &&
            notStarted.length &&
            (row.state === "done" || row.state === "skipped"))
            return {
                ...row,
                state: "warning",
                detail: row.detail ?? servicesNotStartedSentence(notStarted),
            };
        // A red suite that did not stop the run (a first setup activates anyway)
        // is a warning on the tests row, not a failure.
        if (row.key === "tests" &&
            row.state === "done" &&
            suiteResult?.status === "failed")
            return {
                ...row,
                state: "warning",
                detail: suiteResult.headSha
                    ? `Tests fail on ${suiteResult.headSha.slice(0, 7)}: Wright's verify will fail too`
                    : "Tests fail on this commit: Wright's verify will fail too",
            };
        return row;
    });
}
function recordedRows(planned, validation) {
    const stages = validation?.stages;
    if (stages?.length)
        return planned.map((row) => {
            const stage = stages.find((entry) => entry.key === row.key);
            const detail = row.detail ?? stage?.message;
            return {
                ...row,
                state: stage?.state ?? "pending",
                ...(detail ? { detail } : {}),
            };
        });
    const reached = validation ? legacyStageKey(validation.stage) : null;
    if (!reached)
        return planned.map((row) => ({ ...row, state: "pending" }));
    // A finished run has done everything but put the environment to use.
    if (reached === "end")
        return planned.map((row) => ({
            ...row,
            state: row.key === "use" ? "pending" : "done",
        }));
    const index = planned.findIndex((row) => row.key === reached);
    return planned.map((row, position) => ({
        ...row,
        state: position < index
            ? "done"
            : position > index
                ? "pending"
                : validation.status === "needs_attention"
                    ? "failed"
                    : validation.status === "ready"
                        ? "done"
                        : "running",
    }));
}
//# sourceMappingURL=qaChecklist.js.map